import { Link, useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { useAuth } from "./hooks/IsAuth";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "./slices/categorySlice";
import { callAPIWithoutAuth } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { fetchFooterData, selectCompanyInfo, selectContactInfo, selectCopyright, selectFooterError, selectFooterLoading, selectPaymentMethods, selectPoweredByLink, selectPoweredByText, selectSocialLinks } from "./slices/footerSlice";
import { loginModelOpen } from "./common/Helper";

function Footer() {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const { isLoggedIn } = useAuth()
  const companyInfo = useSelector(selectCompanyInfo);
  const socialLinks = useSelector(selectSocialLinks);
  const contactInfo = useSelector(selectContactInfo);
  const paymentMethods = useSelector(selectPaymentMethods);
  const copyright = useSelector(selectCopyright);
  const footerLoading = useSelector(selectFooterLoading);
  const footerError = useSelector(selectFooterError);
  const poweredByLink = useSelector(selectPoweredByLink);
  const poweredByText = useSelector(selectPoweredByText);
  const { categories, loading: categoriesLoading } = useSelector(
    (state) => state.category || { categories: [], loading: false }
  );

  const [contentPages, setContentPages] = React.useState([]);
  const [contentLoading, setContentLoading] = React.useState(false);
  const [contentError, setContentError] = React.useState(null);

  const fetchContentPages = async () => {
    setContentLoading(true);
    setContentError(null);

    try {
      const response = await callAPIWithoutAuth(
        apiUrls.contentPageListing,
        {},
        "GET",
        {}
      );

      if (response?.data?.success) {
        const data = response.data.data;
        setContentPages(data || []);
      } else {
        setContentError('Failed to fetch content pages');
        console.error('Failed to fetch content pages:', response?.data?.message);
      }
    } catch (error) {
      setContentError('Network error while fetching content');
      console.error('Error fetching content pages:', error);
    } finally {
      setContentLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchFooterData());
    fetchContentPages();
  }, [dispatch]);

  const handleCategoryClick = (category) => {
    navigate(`/product-listing/${category?.id}`);
  };


  const FooterSkeleton = () => (
    <footer className="bg-dark pt-5">
      <div className="container">
        <div className="row">
          <div className="col-lg-4 mb-4 mb-lg-0">
            <div className="footer-content">
              <div className="mb-4">
                <div className="skeleton-shimmer rounded-3" style={{ width: '150px', height: '50px' }}></div>
              </div>
              <div className="mb-4">
                <div className="skeleton-shimmer rounded-3 w-100" style={{ height: '60px' }}></div>
              </div>
              <div className="social-sec">
                <div className="skeleton-shimmer rounded-3 mb-3" style={{ width: '120px', height: '24px' }}></div>
                <div className="d-flex gap-3 mt-3">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i}
                      className="skeleton-shimmer rounded-circle"
                      style={{ width: '36px', height: '36px' }}>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <div className="row">
              {[1, 2, 3].map(col => (
                <div key={col} className="col-md-4 col-6 mb-4">
                  <div className="shot_menu">
                    <div className="skeleton-shimmer rounded-3 mb-3" style={{ width: '100px', height: '24px' }}></div>
                    <div className="short_menu_items">
                      {[1, 2, 3, 4, 5].map(item => (
                        <div key={item}
                          className="skeleton-shimmer rounded-3 mb-2"
                          style={{ width: item == 3 ? '130px' : '150px', height: '18px' }}>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="row mt-4 pt-3 border-top border-secondary">
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="d-flex align-items-center gap-3">
              <div className="skeleton-shimmer rounded-3" style={{ width: '250px', height: '40px' }}></div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-md-end">
              <div className="skeleton-shimmer rounded-3" style={{ width: '200px', height: '40px' }}></div>
            </div>
          </div>
        </div>
        <div className="row mt-4 pt-3 border-top border-secondary">
          <div className="col-md-6 mb-2 mb-md-0">
            <div className="skeleton-shimmer rounded-3" style={{ width: '180px', height: '20px' }}></div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-md-end">
              <div className="skeleton-shimmer rounded-3" style={{ width: '150px', height: '20px' }}></div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );

  if (footerLoading || categoriesLoading || contentLoading) {
    return <FooterSkeleton />;
  }

  if (footerError || contentError) {
    return (
      <footer>
        <div className="container">
          <div className="alert alert-danger" role="alert">
            Failed to load footer content. Please refresh the page.
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer>
      <div className="container">
        <div className="row">
          <div className="col-lg-4">
            <div className="footer-content">
              <div className="flogo mb-4">
                <img src={companyInfo?.logo || "/image/footer-logo2.png"} alt="Company Logo" />
              </div>
              <div className="footer-inner-content mb-40">
                <p className="mb-0">
                  {companyInfo?.description || "Upgrade your drive with premium tyres and high-quality windshields designed for maximum safety, comfort, and long-lasting performance."}                </p>
              </div>
              <div className="social-sec">
                <h5>Connecting on Social Media</h5>
                <div className="footer-icons d-flex gap-2 mt-4">
                  {socialLinks?.filter(link => link.active).map((link, index) => (
                    <Link key={index} to={link.url !== '#' ? link.url : '#'} target={link.url !== '#' ? "_blank" : ''} rel="noopener noreferrer">
                      <img src={link.icon} alt={link.platform} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <div className="short-links d-flex flex-wrap gap-3 justify-content-lg-around justify-content-between">
              <div className="shot_menu">
                <h5>Quick Links</h5>
                <div className="short_menu_items">
                  <Link to="/" className="d-block"><img src="/image/f-angle-right.svg" alt="" /> Home</Link>
                  <Link to="/about" className="d-block"><img src="/image/f-angle-right.svg" alt="" /> About Us</Link>
                  {categories && categories.length > 0 ? (
                    categories.map(category => (
                      <Link to="#" onClick={(e) => {
                        e.preventDefault();
                        handleCategoryClick(category);
                      }} className="d-block"><img src="/image/f-angle-right.svg" alt="" />  {category?.title}</Link>))
                  ) : (
                    <p className="text-muted">No categories available</p>
                  )}

                  <Link to="/contact" className="d-block"><img src="/image/f-angle-right.svg" alt="" /> Contact Us</Link>
                </div>
              </div>
              <div className="shot_menu">
                <h5>My Account</h5>
                <div className="short_menu_items">
                  {<Link to={isLoggedIn ? "/dashboard" : '#'}
                    className="d-block"
                    onClick={(e) => {
                      if (!isLoggedIn) {
                        e.preventDefault();
                        loginModelOpen();
                      }
                    }}><img src="/image/f-angle-right.svg" alt="" /> My Profile</Link>
                  }
                  {<Link to={isLoggedIn ? 'my-order' : "#"} className="d-block" onClick={(e) => {
                    if (!isLoggedIn) {
                      e.preventDefault();
                      loginModelOpen();
                    }
                  }}><img src="/image/f-angle-right.svg" alt="" /> Order History</Link>}
                  {<Link to={isLoggedIn ? "/wishlist" : "#"} className="d-block" onClick={(e) => {
                    if (!isLoggedIn) {
                      e.preventDefault();
                      loginModelOpen();
                    }
                  }}><img src="/image/f-angle-right.svg" alt="" /> My Wishlist</Link>}
                  <Link to="/view-cart" className="d-block"><img src="/image/f-angle-right.svg" alt="" /> Shopping cart</Link>
                  
                </div>
              </div>
              <div className="shot_menu">
                <h5>Support</h5>
                <div className="short_menu_items">
                  {contentPages?.length > 0 ? (
                    contentPages.map((it, i) => (
                      <Link key={i} to={`/cms/${it?.slug}`} className="d-block">
                        <img src="/image/f-angle-right.svg" alt="" /> {it?.title}
                      </Link>
                    ))
                  ) : (
                    <p className="text-muted">No support pages available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="phone-email d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div className="phone-info d-flex flex-wrap align-items-center gap-2 gap-lg-5">
            <Link to={`tel:${contactInfo?.phone}`}>
              <img src={contactInfo?.phoneIcon || "/image/f-calling.svg"} alt="Phone" className="me-2" />
              {contactInfo?.phone || "1869 661 9336"}
            </Link>
            <Link to={`mailto:${contactInfo?.email}`}>
              <img src={contactInfo?.emailIcon || "/image/f-message.svg"} alt="Email" className="me-2" />
              {contactInfo?.email || "islandmotors@gmail.com"}
            </Link>
          </div>
          <div className="payment-info d-flex align-items-center gap-5">
            {paymentMethods?.filter(method => method.active).map((method, index) => (
              <img key={index} src={method.icon} className="img-fluid" alt={method.name} />
            ))}
          </div>
        </div>
        <div className="copy-right d-flex flex-wrap gap-3 align-md-items-center justify-content-between">
          <p>{copyright}</p>
          <p> <Link to={poweredByLink} target="_blank">{poweredByText}</Link></p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;